import { CheckCircleIcon, TrashIcon, XCircleIcon } from '@phosphor-icons/react';

import { ActionMenu } from '@/components/common/action-menu';
import { DeleteConfirmModal } from '@/components/feedback/dialog';

import type { DiscountDetail } from '../types';
import { useDiscountDeleteConfirm, useDiscountStatusAction } from '../use-discount-actions';

export type DiscountMoreOptionsProps = {
  discount: DiscountDetail;
  /** Called after a successful delete (detail page navigates back to the listing). */
  onDeleted: () => void;
};

/** Detail-header "more options" kebab — Enable/Disable/Delete keyed by status. Composes the
 *  ticket-04 hooks + shared ActionMenu/DeleteConfirmModal (no new mutation/dialog code). The
 *  status switch mirrors the listing row menu; kept as a small local duplicate rather than
 *  churning the in-flight listing file. */
export function DiscountMoreOptions({ discount, onDeleted }: DiscountMoreOptionsProps) {
  const { handleEnable, handleDisable } = useDiscountStatusAction();
  const { setDeleting, deleteModalProps } = useDiscountDeleteConfirm({ onSuccess: onDeleted });

  const del = {
    key: 'delete',
    label: 'Delete',
    icon: <TrashIcon />,
    danger: true,
    onClick: () => setDeleting(discount),
  };
  const toggle =
    discount.status === 'active'
      ? {
          key: 'disable',
          label: 'Disable',
          icon: <XCircleIcon />,
          onClick: () => void handleDisable(discount),
        }
      : {
          key: 'enable',
          label: 'Enable',
          icon: <CheckCircleIcon />,
          onClick: () => void handleEnable(discount),
        };
  const items = discount.status === 'expired' ? [del] : [toggle, del];

  return (
    <>
      <ActionMenu horizontal triggerLabel={`Actions for ${discount.name}`} groups={[{ items }]} />
      <DeleteConfirmModal {...deleteModalProps} />
    </>
  );
}
